import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import {Box, Button} from "@material-ui/core";
import {grey} from "@material-ui/core/colors";
import clsx from "clsx";
import DeleteIcon from "@material-ui/icons/Delete";
import type {IGridSchema, IGridSchemaCell} from "ts/api/grid-schema";
import {Grid as GridType, GridForm} from "ts/api/task";
import {UseFieldArrayRemove, UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import RowTitles from "view/components/GridResponseBlock/CustomizeTable/Components/Rows/RowTitles";
import {ActiveCells, ErrorCell, rowsName} from "../../tools";
import Cell from "./Cell";
import useStyles from "./styles";

export type RowsProps = {
  openSettings?: (rowIndex: number, columnIndex: number) => void;
  errorCells?: ErrorCell[];
  activeCells?: ActiveCells;
  areAllCellsMandatory?: boolean;
  setAreAllCellsMandatory?: Dispatch<SetStateAction<boolean>>;
  viewOnly?: boolean;
  cellsBeforeMarkingMandatory?: React.MutableRefObject<
    IGridSchema["cells"] | null
  >;
  isResponseMode?: boolean;
  /**
   * Loose shape — only `gridSchema` + `grid` are consumed here, so both
   * the legacy `TaskExecution | Multitask` union and the new
   * `ResponseFormTask` satisfy it without an adapter.
   */
  task?: {
    gridSchema?: IGridSchema | null;
    grid?: GridType | string;
  };
  isJustSavedDraft?: React.MutableRefObject<boolean>;
  openParagraph?: (rowIndex: number, columnIndex: number) => void;
  customRemoveRow?: (rowIndex: number) => void;
  removeRowField?: UseFieldArrayRemove;
  /** Resolved by the parent `Grid` (prop → live definition), see `Columns`. */
  includeRowTitles?: boolean;
};

function Rows({
  openSettings,
  activeCells,
  areAllCellsMandatory,
  setAreAllCellsMandatory,
  viewOnly,
  cellsBeforeMarkingMandatory,
  isResponseMode,
  task,
  isJustSavedDraft,
  openParagraph,
  customRemoveRow,
  removeRowField,
  errorCells,
  includeRowTitles: includeRowTitlesProp,
}: RowsProps) {
  const classes = useStyles();
  // NOTE: I don't know why correct type (`TaskExecution | Multitask`) breaks the whole build and
  // ts server. Need to resolve in future
  const form = useContext(FormContext) as UseFormReturn<GridForm>;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {watch, setValue} = form;

  const cellLength = task?.gridSchema?.cells
    ? Object.keys(task.gridSchema.cells).length
    : 0;

  const gridSchemaCells = watch("gridSchema.cells");
  const cells = useMemo(() => {
    const currentCells = task?.gridSchema?.cells || gridSchemaCells;
    const rowsLength = Object.entries(currentCells).length;
    const executedTaskRowsLength = Array.isArray(task?.grid)
      ? task?.grid.length
      : 0;
    const lengthDiff = Number(executedTaskRowsLength) - rowsLength;

    if (lengthDiff > 0) {
      const obj: Record<number, IGridSchemaCell[]> = {};
      for (let i = 0; i < lengthDiff; i += 1) {
        obj[i + rowsLength] = currentCells[rowsLength - 1];
      }
      return {...currentCells, ...obj};
    }
    return currentCells;
  }, [
    task?.gridSchema?.cells,
    task?.grid,
    task?.grid?.length,
    gridSchemaCells,
  ]);

  const rows = task?.gridSchema?.rows || watch("gridSchema.rows");
  const includeRowTitles =
    includeRowTitlesProp ??
    task?.gridSchema?.includeRowTitles ??
    watch("gridSchema.includeRowTitles");
  const additionalRowNames = watch("additionalRowNames");

  const makeRowMandatory = useCallback(
    (rowIndex: number) => {
      if (!cells) {
        return;
      }
      const copyCells = {...cells};

      const [mandatories, nonMandatories] = copyCells[rowIndex]?.reduce<
        [number, number]
      >(
        (acc, cell) =>
          cell?.required ? [acc[0] + 1, acc[1]] : [acc[0], acc[1] + 1],
        [0, 0]
      );
      const isRequired = mandatories < nonMandatories;
      for (let i = 0; i < copyCells[rowIndex].length; i += 1) {
        copyCells[rowIndex][i].required = isRequired;
      }
      if (!isRequired) {
        setAreAllCellsMandatory?.(false);
      }
      if (cellsBeforeMarkingMandatory) {
        cellsBeforeMarkingMandatory.current = null;
      }
      setValue("gridSchema.cells", copyCells, {shouldDirty: true});
    },
    [cells, cellsBeforeMarkingMandatory, setAreAllCellsMandatory, setValue]
  );

  const makeMandatory = useCallback(
    (rowIndex: number, columnIndex: number) => {
      const editedCell = cells?.[rowIndex]?.[columnIndex];
      if (!editedCell) {
        return;
      }
      editedCell.required = !editedCell.required;
      const cellsCopy = {...cells};
      cellsCopy[rowIndex][columnIndex] = editedCell;

      setValue("gridSchema.cells", cellsCopy, {shouldDirty: true});
      if (
        areAllCellsMandatory &&
        !editedCell.required &&
        setAreAllCellsMandatory
      ) {
        setAreAllCellsMandatory(false);
      }
      if (cellsBeforeMarkingMandatory) {
        cellsBeforeMarkingMandatory.current = null;
      }
    },
    [
      areAllCellsMandatory,
      cells,
      cellsBeforeMarkingMandatory,
      setAreAllCellsMandatory,
      setValue,
    ]
  );

  const removeRow = useCallback(
    (rowIndex: number) => {
      if (customRemoveRow) {
        return customRemoveRow(rowIndex);
      }
      if (!cells || !rows || !removeRowField) {
        return;
      }
      removeRowField(rowIndex);
      const copyCells = {...cells};
      delete copyCells[rowIndex];
      for (let i = rowIndex; i < rows.length - 1; i += 1) {
        copyCells[i] = copyCells[i + 1];
      }
      delete copyCells[rows.length - 1];
      setValue("gridSchema.cells", copyCells, {shouldDirty: true});
      if (cellsBeforeMarkingMandatory) {
        cellsBeforeMarkingMandatory.current = null;
      }
    },
    [
      cells,
      cellsBeforeMarkingMandatory,
      customRemoveRow,
      removeRowField,
      rows,
      setValue,
    ]
  );

  const cellEntries = useMemo(() => Object.entries(cells), [cells, cellLength]);
  const isDeleteDisabled = cellEntries.length <= 1;

  const isNotViewOnlyAndResponse = !viewOnly && !isResponseMode;

  const isSmallRowTitles =
    isResponseMode && includeRowTitles && !rows?.some((row) => !!row.name);

  return (
    <FlexBox column>
      {cellEntries?.map(([_, row], rowIndex) => {
        const name = `${rowsName}.${rowIndex}.name` as const;
        const isActive =
          ((activeCells?.columnIndex === 0 || activeCells?.isFullRow) &&
            activeCells?.rowIndex === rowIndex) ||
          (activeCells?.columnIndex === 0 && activeCells?.isFullColumn);

        const lastRowName =
          rows?.[rowIndex]?.name ??
          additionalRowNames?.[rowIndex - rows.length] ??
          "";

        const isMoreRowsThanSetUp = !!(
          rows?.length &&
          isResponseMode &&
          rows.length - 1 < rowIndex
        );

        const showDeleteRow =
          !viewOnly && (!isResponseMode || isMoreRowsThanSetUp);

        const hasTitleCell = !!includeRowTitles && !isSmallRowTitles;

        return (
          <FlexBox key={rowIndex}>
            {hasTitleCell && (
              <RowTitles
                name={name}
                rows={rows}
                viewOnly={viewOnly}
                isResponseMode={isResponseMode}
                isNotViewOnlyAndResponse={isNotViewOnlyAndResponse}
                isActive={isActive}
                lastRowName={lastRowName}
                makeRowMandatory={makeRowMandatory}
                rowIndex={rowIndex}
                textareaRef={textareaRef}
                showDelete={showDeleteRow}
                isDeleteDisabled={isDeleteDisabled}
                removeRow={removeRow}
              />
            )}
            {includeRowTitles && isSmallRowTitles && (
              <Box className={classes.smallRowTitle} />
            )}
            {row?.map((cell, columnIndex) => (
              <Cell
                includeRowTitles={includeRowTitles}
                key={columnIndex}
                cell={cell}
                onClickMandatory={() => makeMandatory(rowIndex, columnIndex)}
                onClickSettings={() => openSettings?.(rowIndex, columnIndex)}
                activeCells={activeCells}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                viewOnly={viewOnly}
                isResponseMode={isResponseMode}
                isJustSavedDraft={isJustSavedDraft}
                openParagraph={openParagraph}
                errorCells={errorCells}
              />
            ))}
            {showDeleteRow && !hasTitleCell && (
              <Button
                disabled={isDeleteDisabled}
                className={classes.deleteRowButtonStandalone}
                onClick={() => removeRow(rowIndex)}
              >
                <DeleteIcon
                  className={clsx(
                    classes.icon,
                    isDeleteDisabled && classes.disableDeleteRowSVG
                  )}
                  htmlColor={grey[500]}
                />
              </Button>
            )}
          </FlexBox>
        );
      })}
    </FlexBox>
  );
}

export default Rows;
